<template>
  <ConditionalWrapper
    :skip="isButtonGroup"
    v-bind="props.wrapperAttrs"
    :class="computedWrapperClasses"
  >
    <input
      :id="computedId"
      ref="_input"
      v-model="localValue"
      :class="computedInputClasses"
      type="checkbox"
      :disabled="props.disabled || parentData?.disabled.value || formGroupData?.disabled.value"
      :required="computedRequired || undefined"
      :name="props.name || parentData?.name.value"
      :form="props.form || parentData?.form.value"
      :aria-label="props.ariaLabel"
      :aria-labelledby="props.ariaLabelledby"
      :aria-required="computedRequired || undefined"
      :value="props.value"
      :true-value="props.value"
      :false-value="props.uncheckedValue"
      :indeterminate="indeterminate || undefined"
      v-bind="processedAttrs.inputAttrs"
    />
    <label v-if="hasDefaultSlot || !resolvedPlain" :for="computedId" :class="labelClasses">
      <slot />
    </label>
  </ConditionalWrapper>
</template>

<script setup lang="ts">
import {useFocus} from '@vueuse/core'
import {computed, inject, useAttrs, useTemplateRef} from 'vue'
import {getClasses, getInputClasses, getLabelClasses} from '../../composables/useFormCheck'
import {checkboxGroupKey, formGroupKey} from '../../utils/keys'
import ConditionalWrapper from '../ConditionalWrapper.vue'
import {isEmptySlot} from '../../utils/dom'
import {useDefaults} from '../../composables/useDefaults'
import {useId} from '../../composables/useId'
import type {BFormCheckboxProps, BFormCheckboxSlots, CheckboxValue} from '../../types'

defineOptions({
  inheritAttrs: false,
})

const _props = withDefaults(
  defineProps<Omit<BFormCheckboxProps, 'modelValue' | 'indeterminate'>>(),
  {
    wrapperAttrs: undefined,
    inputClass: undefined,
    ariaLabel: undefined,
    ariaLabelledby: undefined,
    autofocus: false,
    button: undefined,
    buttonGroup: false,
    buttonVariant: undefined,
    disabled: false,
    form: undefined,
    id: undefined,
    inline: undefined,
    name: undefined,
    plain: undefined,
    required: false,
    reverse: undefined,
    size: undefined,
    state: undefined,
    switch: undefined,
    uncheckedValue: false,
    value: true,
  }
)
const props = useDefaults(_props, 'BFormCheckbox')
const slots = defineSlots<BFormCheckboxSlots>()
const attrs = useAttrs()

const modelValue = defineModel<BFormCheckboxProps['modelValue']>({
  default: undefined,
})
const indeterminate = defineModel<Exclude<BFormCheckboxProps['indeterminate'], undefined>>(
  'indeterminate',
  {
    default: false,
  }
)

const processedAttrs = computed(() => {
  const {class: wrapperClass, ...inputAttrs} = attrs
  return {wrapperClass, inputAttrs}
})

const computedId = useId(() => props.id, 'form-check')

const parentData = inject(checkboxGroupKey, null)
const formGroupData = inject(formGroupKey, null)?.(computedId)

const input = useTemplateRef('_input')

const {focused} = useFocus(input, {
  initialValue: props.autofocus,
})

const hasDefaultSlot = computed(() => !isEmptySlot(slots.default))

// True default values for props that are undefined to support inheritance
const propDefaults = {
  plain: false,
  button: false,
  inline: false,
  reverse: false,
  switch: false,
  size: undefined,
  buttonVariant: 'secondary' as const,
  state: null,
}

// Single source of truth for resolved prop values with parent inheritance and defaults
const resolvedProps = computed(() => ({
  plain: props.plain ?? parentData?.plain.value ?? propDefaults.plain,
  button: props.button ?? parentData?.buttons.value ?? propDefaults.button,
  inline: props.inline ?? parentData?.inline.value ?? propDefaults.inline,
  reverse: props.reverse ?? parentData?.reverse.value ?? propDefaults.reverse,
  switch: props.switch ?? parentData?.switch.value ?? propDefaults.switch,
  state: props.state ?? parentData?.state.value ?? propDefaults.state,
  size: props.size ?? parentData?.size.value ?? propDefaults.size,
  buttonVariant:
    props.buttonVariant ?? parentData?.buttonVariant.value ?? propDefaults.buttonVariant,
}))

// Shorthand for template usage
const resolvedPlain = computed(() => resolvedProps.value.plain)

const localValue = computed({
  get: () => (parentData ? parentData.modelValue.value : modelValue.value),
  set: (newVal) => {
    if (newVal === undefined) return
    // Indeterminate is implicitly cleared when the checked state is changed to any value
    //  by the user.  We reflect that here by setting our indetermiate model to false
    //  which will emit the indeterminate event to the parent
    indeterminate.value = false
    if (parentData !== null && Array.isArray(newVal)) {
      // The type cast isn't perfect. Array.isArray detects CheckboxValue.unknown[],
      // but since it's parentData, it should always be CheckboxValue[]
      // It doesn't quite work when props.value is an [], but this is more of a Vue issue
      parentData.modelValue.value = newVal as CheckboxValue[]
      return
    }
    modelValue.value = newVal as CheckboxValue
  },
})

const computedRequired = computed(
  () => !!(props.name ?? parentData?.name.value) && (props.required || parentData?.required.value)
)

const isButtonGroup = computed(() => props.buttonGroup || (parentData?.buttons.value ?? false))

const classesObject = computed(() => ({
  ...resolvedProps.value,
  hasDefaultSlot: hasDefaultSlot.value,
}))
const wrapperClasses = getClasses(classesObject)
const computedWrapperClasses = computed(() => [
  wrapperClasses.value,
  processedAttrs.value.wrapperClass,
])
const inputClasses = getInputClasses(classesObject)
const computedInputClasses = computed(() => [inputClasses.value, props.inputClass])
const labelClasses = getLabelClasses(classesObject)

defineExpose({
  blur: () => {
    focused.value = false
  },
  element: input,
  focus: () => {
    focused.value = true
  },
})
</script>
